import { useState, useEffect, useRef } from "react";
import PrivacyPolicy from "./PrivacyPolicy";
import { db } from "./firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const C = {
  bg: "#FAF7F2",
  white: "#FFFFFF",
  orange: "#E87C0C",
  orangeHover: "#C96A08",
  orangeLight: "#FEF3E2",
  green: "#2E9B4B",
  greenLight: "#E8F5EB",
  purple: "#6B3FD4",
  dark: "#1A1208",
  charcoal: "#2D2416",
  muted: "#5C4D3C",
  border: "#EDE5D8",
  wa: "#25D366",
  waHover: "#1ebe5d",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Jost', sans-serif; background: #FAF7F2; color: #1A1208; }
  .hero-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
  .phones-row { display: flex; justify-content: center; align-items: flex-end; gap: 24px; flex-wrap: wrap; }
  .feat2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
  .feat3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; }
  .pricing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; align-items: start; }
  .testi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  .student-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
  .footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; }
  .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .hero-phone { display: flex; justify-content: center; }
  .feat-card { background: #fff; border: 1px solid #EDE5D8; border-radius: 16px; padding: 28px 24px; transition: box-shadow 0.25s, border-color 0.25s, transform 0.25s; cursor: default; height: 100%; }
  .feat-card:hover { box-shadow: 0 8px 32px rgba(232,124,12,0.13); border-color: #E87C0C; transform: translateY(-4px); }
  .feat-card-dark { background: #2D2416; border: 1px solid #3d3020; border-radius: 16px; padding: 28px 24px; transition: box-shadow 0.25s, border-color 0.25s, transform 0.25s; cursor: default; height: 100%; }
  .feat-card-dark:hover { box-shadow: 0 8px 32px rgba(232,124,12,0.2); border-color: #E87C0C; transform: translateY(-4px); }
  .step-card { background: #fff; border-radius: 20px; padding: 36px 28px; text-align: center; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
  .stats-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 24px; text-align: center; max-width: 1000px; margin: 0 auto; }
  .nav-links a { color: #1A1208; text-decoration: none; font-size: 15px; font-weight: 500; transition: color 0.2s; }
  .nav-links a:hover { color: #E87C0C; }
  .reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.6s ease, transform 0.6s ease; }
  .reveal.visible { opacity: 1; transform: translateY(0); }
  .sec { padding: 96px 24px; }
  .hero-sec { padding: 96px 20px 64px; }
  @media (max-width: 900px) {
    .hero-grid { grid-template-columns: 1fr; gap: 40px; }
    .hero-phone { display: none; }
    .hero-sec { padding: 96px 20px 48px; }
    .feat2 { grid-template-columns: 1fr; }
    .feat3 { grid-template-columns: repeat(2, 1fr); }
    .steps-grid { grid-template-columns: repeat(2, 1fr); }
    .pricing-grid { grid-template-columns: 1fr; }
    .testi-grid { grid-template-columns: 1fr; }
    .student-grid { grid-template-columns: 1fr; gap: 32px; }
    .footer-grid { grid-template-columns: 1fr 1fr; gap: 32px; }
    .stats-grid { grid-template-columns: repeat(2,1fr); gap: 20px; }
    .sec { padding: 72px 20px; }
  }
  @media (max-width: 600px) {
    .hero-sec { padding: 90px 16px 40px; }
    .feat2 { grid-template-columns: 1fr; }
    .feat3 { grid-template-columns: 1fr; }
    .steps-grid { grid-template-columns: 1fr; }
    .contact-grid { grid-template-columns: 1fr; }
    .footer-grid { grid-template-columns: 1fr; gap: 24px; }
    .phones-row { flex-direction: column; align-items: center; gap: 36px; }
    .stats-grid { grid-template-columns: repeat(2,1fr); gap: 14px; }
    .sec { padding: 56px 16px; }
  }
`;

/* ── QR grid data (deterministic, never changes) ── */
const QR_DATA = (() => {
  const S = 11;
  const cells = Array(S * S).fill(0);
  const set = (r, c, v = 1) => { if (r >= 0 && r < S && c >= 0 && c < S) cells[r * S + c] = v; };
  const finder = (sr, sc) => {
    for (let r = 0; r < 5; r++) for (let c = 0; c < 5; c++)
      set(sr + r, sc + c, (r === 0 || r === 4 || c === 0 || c === 4 || (r === 2 && c === 2)) ? 1 : 0);
  };
  finder(0, 0); finder(0, 6); finder(6, 0);
  for (let i = 0; i < S * S; i++) {
    const r = Math.floor(i / S), c = i % S;
    const inFinder = (r < 5 && c < 5) || (r < 5 && c >= 6) || (r >= 6 && c < 5);
    if (!inFinder && cells[i] === 0) cells[i] = (i * 13 + r * 7 + c * 11) % 3 === 0 ? 1 : 0;
  }
  return cells;
})();

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0 }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={"reveal" + (visible ? " visible" : "")} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function PhoneShell({ children, scale = 1 }) {
  return (
    <div style={{
      width: 220 * scale, height: 440 * scale, borderRadius: 36 * scale,
      background: "#111", boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
      overflow: "hidden", border: "8px solid #222", position: "relative", flexShrink: 0
    }}>
      <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 60 * scale, height: 18 * scale, background: "#111", borderRadius: "0 0 12px 12px", zIndex: 10 }} />
      <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>{children}</div>
    </div>
  );
}

/* ── VENDOR DASHBOARD SCREEN ── */
function VendorDashScreen() {
  return (
    <div style={{ background: "#FAF4EE", height: "100%", fontFamily: "'Jost',sans-serif", overflow: "hidden", fontSize: 10 }}>
      {/* Header */}
      <div style={{ padding: "28px 12px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", border: `2px solid ${C.orange}`, background: "#ddd", overflow: "hidden", flexShrink: 0 }}>
            <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#c8a882,#a07850)" }} />
          </div>
          <div>
            <div style={{ fontSize: 7, color: C.muted }}>Welcome,</div>
            <div style={{ fontWeight: 700, fontSize: 9 }}>Ghar Ka Swaad</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, background: C.greenLight, borderRadius: 20, padding: "3px 8px" }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: C.orange }} />
          <span style={{ fontSize: 8, color: C.green, fontWeight: 700 }}>Open</span>
        </div>
      </div>
      {/* Stats row */}
      <div style={{ display: "flex", margin: "0 10px 8px", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        {[
          { n: "0", l: "INTEREST\nTODAY" },
          { n: "0", l: "EXPIRING\nTODAY" },
          { n: "★ N/A", l: "RATING &\nREVIEWS", orange: true },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: "8px 4px", textAlign: "center", borderRight: i < 2 ? `1px solid ${C.border}` : "none" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: s.orange ? C.orange : C.dark }}>{s.n}</div>
            <div style={{ fontSize: 6, color: C.muted, marginTop: 2, lineHeight: 1.3, whiteSpace: "pre-line" }}>{s.l}</div>
          </div>
        ))}
      </div>
      {/* Cards */}
      {[
        { icon: "🧾", title: "Orders", sub: "View all customer orders" },
        { icon: "👥", title: "Subscriptions", sub: "Manage subscriber approvals" },
      ].map(c => (
        <div key={c.title} style={{ margin: "0 10px 6px", background: "#fff", borderRadius: 10, padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14 }}>{c.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 9 }}>{c.title}</div>
              <div style={{ fontSize: 7, color: C.muted }}>{c.sub}</div>
            </div>
          </div>
          <span style={{ fontSize: 9, color: C.muted }}>›</span>
        </div>
      ))}
      {/* Quick Actions */}
      <div style={{ padding: "6px 12px 4px", fontWeight: 700, fontSize: 9 }}>Quick Actions</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, padding: "0 10px" }}>
        {[
          { icon: "🍽️", label: "Add Rice Plate", bg: "#FEF0E6" },
          { icon: "⭐", label: "Special Menu", bg: "#E8F5EB" },
          { icon: "👥", label: "Attendance", bg: "#FEF0E6" },
          { icon: "💰", label: "Expenses", bg: "#E8F5EB" },
        ].map(a => (
          <div key={a.label} style={{ background: "#fff", borderRadius: 10, padding: "8px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: a.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, margin: "0 auto 4px" }}>{a.icon}</div>
            <div style={{ fontSize: 7, fontWeight: 600 }}>{a.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── QR CODE SCREEN ── */
function QRScreen() {
  return (
    <div style={{ background: "#F0F4FF", height: "100%", fontFamily: "'Jost',sans-serif", overflow: "hidden", fontSize: 10 }}>
      <div style={{ padding: "36px 14px 0", textAlign: "center" }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: C.dark, marginBottom: 4 }}>Your Permanent QR Code</div>
        <div style={{ fontSize: 8, color: "#888", marginBottom: 16, lineHeight: 1.4 }}>Print once · Stick at entrance · Never changes</div>
        <div style={{ background: "#fff", border: "2px solid #4A90D9", borderRadius: 12, padding: 10, display: "inline-block", marginBottom: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(11, 9px)", gap: "1px", background: "#fff" }}>
            {QR_DATA.map((f, i) => <div key={i} style={{ width: 9, height: 9, background: f ? "#111" : "#fff" }} />)}
          </div>
        </div>
        <div style={{ background: "#EEF3FF", borderLeft: "3px solid #4A90D9", borderRadius: "0 8px 8px 0", padding: "8px 10px", textAlign: "left", fontSize: 8, color: "#4A6FA5", lineHeight: 1.5 }}>
          📌 Users scan this QR to mark attendance. Make sure your meal session is started from the dashboard.
        </div>
        <div style={{ marginTop: 14, fontSize: 9, color: "#888" }}>← Back to Dashboard</div>
      </div>
    </div>
  );
}

/* ── MENU SCREEN ── */
function MenuScreen() {
  const items = ["चपाती", "भात", "सोयाबीन रस्सा भाजी", "कोबी सुक्की", "ताक"];
  return (
    <div style={{ background: "#FAF4EE", height: "100%", fontFamily: "'Jost',sans-serif", overflow: "hidden", fontSize: 10 }}>
      <div style={{ padding: "28px 12px 0" }}>
        <div style={{ textAlign: "center", marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: C.dark }}>महाराष्ट्रीयन थाळी</div>
          <div style={{ fontSize: 7, color: C.orange, fontWeight: 600, letterSpacing: "0.08em" }}>FRIDAY, 29 MAY 26</div>
        </div>
        <div style={{ background: "#fff", borderRadius: 10, padding: "8px 10px", marginBottom: 6, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 7, color: C.orange, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 4 }}>● RICE PLATE NAME</div>
          <div style={{ fontSize: 9, color: C.dark }}>महाराष्ट्रीयन थाळी</div>
        </div>
        <div style={{ background: "#fff", borderRadius: 10, padding: "8px 10px", marginBottom: 6, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 7, color: C.orange, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 4 }}>● MENU ITEMS <span style={{ float: "right", background: C.green, color: "#fff", borderRadius: 10, padding: "1px 5px" }}>5</span></div>
          {items.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, background: "#F0FBF4", borderRadius: 6, padding: "4px 8px", marginBottom: 3 }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: C.green, color: "#fff", fontSize: 7, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
              <div style={{ fontSize: 8 }}>{item}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── DOWNLOAD BUTTON ── */
function DlBtn({ icon, store, sub, href, light }) {
  const [hov, setHov] = useState(false);
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        background: light ? (hov ? "#f0f0f0" : "#fff") : (hov ? C.orangeHover : C.orange),
        color: light ? C.dark : "#fff", borderRadius: 12, padding: "12px 22px",
        textDecoration: "none", transition: "background 0.2s, transform 0.15s",
        fontFamily: "'Jost',sans-serif", boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        transform: hov ? "translateY(-2px)" : "none"
      }}>
      <span style={{ fontSize: 22 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 11, opacity: 0.9, lineHeight: 1 }}>{sub}</div>
        <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{store}</div>
      </div>
    </a>
  );
}

/* ── WHATSAPP BUTTON ── */
function WAButton({ label, float }) {
  const [hov, setHov] = useState(false);
  const waLink = "https://wa.me/917030078393?text=Hi%2C%20I%27m%20interested%20in%20SwaadIndia%20for%20my%20mess";
  return (
    <a href={waLink} target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        background: hov ? C.waHover : C.wa, color: "#fff",
        borderRadius: float ? "50px" : 12, padding: float ? "14px 20px" : "12px 22px",
        textDecoration: "none", transition: "background 0.2s, transform 0.15s",
        fontFamily: "'Jost',sans-serif",
        boxShadow: float ? "0 4px 20px rgba(37,211,102,0.4)" : "0 4px 16px rgba(37,211,102,0.3)",
        transform: hov ? "translateY(-2px)" : "none",
        position: float ? "fixed" : "relative", bottom: float ? 24 : "auto", right: float ? 24 : "auto", zIndex: float ? 200 : "auto"
      }}>
      <span style={{ fontSize: float ? 22 : 20 }}>💬</span>
      {label && <div>
        <div style={{ fontSize: 10, opacity: 0.9, lineHeight: 1 }}>Chat on</div>
        <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{label}</div>
      </div>}
    </a>
  );
}

/* ═══════════════════════════════════════════
   MAIN APP
═══════════════════════════════════════════ */
export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [page, setPage] = useState("home");

  useEffect(() => {
    const tag = document.createElement("style");
    tag.textContent = CSS;
    document.head.appendChild(tag);
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => { document.head.removeChild(tag); window.removeEventListener("scroll", onScroll); };
  }, []);

  const scrollTo = id => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); setMenuOpen(false); };

  const navLinks = [
    { label: "Features", id: "features" },
    { label: "How It Works", id: "how" },
    { label: "Pricing", id: "pricing" },
    { label: "For Students", id: "students" },
    { label: "About", id: "about" },
    { label: "Contact", id: "contact" },
  ];

  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [contactSent, setContactSent] = useState(false);

  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState("");

  if (page === "privacy") return <PrivacyPolicy onBack={() => setPage("home")} />;

  const handleContactSubmit = async e => {
    e.preventDefault();
    setContactLoading(true);
    setContactError("");
    try {
      await addDoc(collection(db, "contactMessages"), {
        ...contactForm,
        createdAt: serverTimestamp(),
        source: "main-site",
        read: false,
      });
      setContactSent(true);
      setContactForm({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => setContactSent(false), 5000);
    } catch (err) {
      setContactError("Failed to send. Please try WhatsApp instead.");
    } finally {
      setContactLoading(false);
    }
  };

  const tier1 = [
    { icon: "📱", title: "QR Code Attendance", hi: "Ek scan attendance done.", body: "No register, no disputes. Print one permanent QR code, stick it at the entrance. Works forever. No internet needed to scan." },
    { icon: "👁️", title: "Live Attendance Dashboard", hi: "Present, absent, pending. Live dekho.", body: "Check from your phone at any time. Both lunch and dinner tracked separately in real-time." },
    { icon: "👥", title: "Subscription Management", hi: "Naya request. Ek tap mein approve.", body: "Expired subscribers are automatically deactivated. No more chasing students on WhatsApp for renewals." },
    { icon: "🔛", title: "Open / Close Toggle", hi: "Aaj band? Bas switch off karo.", body: "Students are notified automatically when you close. Stops endless calls. One tap. Sorted." },
  ];

  const tier2 = [
    { icon: "🍽️", title: "Special Menu Posting", body: "Made something special today? Post it. Subscribers get a notification. Drive more footfall without any extra effort." },
    { icon: "🔍", title: "Student Discovery", body: "When a new student arrives in Pune, they search for a mess on the app. Your listing shows up, and subscribers come to you." },
    { icon: "📦", title: "Order Management", body: "Students place orders, you accept or reject. Payment link is auto-sent. No confusion, no follow-up calls needed." },
    { icon: "💰", title: "Expense Tracker", body: "Track daily expenses: vegetables, salary, gas. See your full profit-loss report at the end of every month." },
    { icon: "🌐", title: "Hindi / Marathi Support", body: "The app works in Marathi and Hindi too. Not comfortable in English? Koi baat nahi. We've got you covered." },
  ];

  const steps = [
    { num: "01", icon: "📝", title: "Register Free", body: "2-minute free signup. No credit card required. Udyam registered? Even better, verification is faster." },
    { num: "02", icon: "🍛", title: "Set Up Your Menu", body: "Add your daily menu and set subscription plans. Students get notified automatically. You don't lift a finger." },
    { num: "03", icon: "📈", title: "Grow Your Subscribers", body: "Students find you, subscribe, and scan QR to mark attendance. Everything is tracked. Tum bas khaana banao." },
  ];

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>

      {/* Floating WhatsApp */}
      <WAButton float />

      {/* ═══ NAV ═══ */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? "rgba(250,247,242,0.96)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled ? `1px solid ${C.border}` : "none",
        transition: "all 0.3s"
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", height: 72, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <img src="/swaadIndia_logo.png" alt="SwaadIndia" style={{ height: 52, width: "auto" }} />
          <div className="nav-links" style={{ display: "flex", gap: 32, alignItems: "center" }}>
            {navLinks.map(n => (
              <a key={n.id} href={`#${n.id}`} onClick={e => { e.preventDefault(); scrollTo(n.id); }}>{n.label}</a>
            ))}
            <a href="https://firebasestorage.googleapis.com/v0/b/test-36c7c.firebasestorage.app/o/SwaadIndiaApp.apk?alt=media&token=d60c7ffe-b701-4bc1-b2a4-fa3f4c7af9ab" target="_blank" rel="noopener noreferrer" style={{
              background: C.orange, color: "#fff", border: "none", borderRadius: 10,
              padding: "9px 22px", fontSize: 14, fontWeight: 600, fontFamily: "'Jost',sans-serif", cursor: "pointer",
              textDecoration: "none", display: "inline-block"
            }}>Download App</a>
          </div>
          <button onClick={() => setMenuOpen(o => !o)} className="ham-btn"
            style={{ display: "none", flexDirection: "column", gap: 5, background: "none", border: "none", cursor: "pointer", padding: 4 }}
            aria-label="Menu">
            {[0, 1, 2].map(i => (
              <span key={i} style={{
                display: "block", width: 24, height: 2, background: C.dark, borderRadius: 2,
                transition: "transform 0.3s, opacity 0.3s",
                transform: menuOpen ? (i === 0 ? "translateY(7px) rotate(45deg)" : i === 2 ? "translateY(-7px) rotate(-45deg)" : "none") : "none",
                opacity: menuOpen && i === 1 ? 0 : 1
              }} />
            ))}
          </button>
        </div>
        {menuOpen && (
          <div style={{ background: "#fff", borderTop: `1px solid ${C.border}`, padding: "16px 24px 20px" }}>
            {navLinks.map(n => (
              <div key={n.id} style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                <a href={`#${n.id}`} onClick={e => { e.preventDefault(); scrollTo(n.id); }}
                  style={{ color: C.dark, textDecoration: "none", fontSize: 16, fontWeight: 500 }}>{n.label}</a>
              </div>
            ))}
            <a href="https://firebasestorage.googleapis.com/v0/b/test-36c7c.firebasestorage.app/o/SwaadIndiaApp.apk?alt=media&token=d60c7ffe-b701-4bc1-b2a4-fa3f4c7af9ab" target="_blank" rel="noopener noreferrer" style={{
              marginTop: 14, width: "100%", background: C.orange, color: "#fff", border: "none",
              borderRadius: 10, padding: "12px", fontSize: 15, fontWeight: 600, fontFamily: "'Jost',sans-serif", cursor: "pointer",
              textDecoration: "none", display: "block", textAlign: "center", boxSizing: "border-box"
            }}>Download App</a>
          </div>
        )}
      </nav>
      <style>{`@media(max-width:768px){.nav-links{display:none!important}.ham-btn{display:flex!important}}`}</style>

      {/* ═══ HERO ═══ */}
      <section className="hero-sec" style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div className="hero-grid">
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: C.orangeLight, borderRadius: 20, padding: "6px 14px", marginBottom: 20 }}>
              <span>🍛</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.orange }}>For Mess Owners in Pune</span>
            </div>
            <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(36px,5vw,62px)", lineHeight: 1.1, color: C.dark, marginBottom: 16, fontWeight: 700 }}>
              Manage Your Mess Digitally<br />
              <span style={{ color: C.orange, fontStyle: "italic" }}>90 Days Free, Zero Risk</span>
            </h1>
            <p style={{ fontSize: 17, color: C.muted, lineHeight: 1.7, marginBottom: 12, maxWidth: 480 }}>
              QR attendance, live dashboard, subscription management. All in one app. First 90 days completely free, no credit card required.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.greenLight, borderRadius: 10, padding: "8px 14px", marginBottom: 28, width: "fit-content" }}>
              <span style={{ fontSize: 16 }}>✅</span>
              <span style={{ fontSize: 14, color: C.green, fontWeight: 600 }}>No credit card · No setup fee · Cancel anytime</span>
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
              <a href="https://firebasestorage.googleapis.com/v0/b/test-36c7c.firebasestorage.app/o/SwaadIndiaApp.apk?alt=media&token=d60c7ffe-b701-4bc1-b2a4-fa3f4c7af9ab" target="_blank" rel="noopener noreferrer" style={{
                background: C.orange, color: "#fff", border: "none", borderRadius: 12,
                padding: "14px 28px", fontSize: 16, fontWeight: 700, fontFamily: "'Jost',sans-serif",
                cursor: "pointer", boxShadow: "0 4px 16px rgba(232,124,12,0.35)",
                textDecoration: "none", display: "inline-block"
              }}>📲 Download App</a>
              <WAButton label="WhatsApp" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ display: "flex" }}>
                {["🧑", "👨", "👩", "🧔", "👱"].map((em, i) => (
                  <div key={i} style={{ width: 34, height: 34, borderRadius: "50%", background: `hsl(${28 + i * 12},75%,72%)`, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, marginLeft: i ? -10 : 0 }}>{em}</div>
                ))}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>50+ vendors onboarding</div>
                <div style={{ fontSize: 13, color: C.muted }}>Already growing across Pune</div>
              </div>
            </div>
          </div>
          <div className="hero-phone">
            <PhoneShell scale={1.1}><VendorDashScreen /></PhoneShell>
          </div>
        </div>
      </section>

      {/* ═══ PROBLEM BAND ═══ */}
      <div style={{ background: C.charcoal, padding: "32px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <p style={{ color: "#c8b4a0", fontSize: 17, lineHeight: 1.8 }}>
            ❌ Still writing attendance in a register every day? &nbsp;
            ❌ Hard to track who's subscribed and who's expired? &nbsp;
            ❌ Students keep calling to ask what's on the menu?
          </p>
          <p style={{ color: C.orange, fontSize: 18, fontWeight: 700, marginTop: 12 }}>
            SwaadIndia solves all of this. Ek app mein. ✅
          </p>
        </div>
      </div>

      {/* ═══ STATS BAR ═══ */}
      <div style={{ background: C.orange, padding: "36px 20px" }}>
        <div className="stats-grid">
          {[
            { n: "500+", l: "Vendors Listed" },
            { n: "10K+", l: "Students in Pune" },
            { n: "90 Days", l: "Free Trial" },
            { n: "₹2999/mo", l: "After Trial" },
          ].map(s => (
            <div key={s.l}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 38, fontWeight: 700, color: "#fff", lineHeight: 1 }}>{s.n}</div>
              <div style={{ fontSize: 14, color: "#fff", marginTop: 6, fontWeight: 500 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ TIER 1 FEATURES ═══ */}
      <section id="features" className="sec">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 52 }}>
              <div style={{ display: "inline-block", background: C.orangeLight, color: C.orange, borderRadius: 20, padding: "5px 16px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Core Features</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,4vw,48px)", fontWeight: 700, marginBottom: 12 }}>Features Mess Owners Actually Need</h2>
              <p style={{ color: C.muted, fontSize: 17, maxWidth: 500, margin: "0 auto" }}>These 4 features alone will take your mess fully digital. Nothing extra needed.</p>
            </div>
          </Reveal>
          <div className="feat2">
            {tier1.map((f, i) => (
              <Reveal key={f.title} delay={i * 80}>
                <div className="feat-card">
                  <div style={{ fontSize: 32, marginBottom: 14 }}>{f.icon}</div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, marginBottom: 6, color: C.dark }}>{f.title}</h3>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.orange, marginBottom: 10, fontStyle: "italic" }}>{f.hi}</div>
                  <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.65 }}>{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ APP SCREENSHOTS ═══ */}
      <section style={{ background: C.orangeLight }} className="sec">
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <div style={{ display: "inline-block", background: "#fff", color: C.orange, borderRadius: 20, padding: "5px 16px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Real App Screenshots</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,4vw,48px)", fontWeight: 700, marginBottom: 12 }}>This Is the App You'll Run Your Mess With</h2>
              <p style={{ color: C.muted, fontSize: 17, maxWidth: 480, margin: "0 auto" }}>Simple, fast, and built specifically for Pune mess owners.</p>
            </div>
          </Reveal>
          <div className="phones-row">
            {[
              { Screen: VendorDashScreen, label: "Vendor Dashboard", sub: "Orders, subscriptions, stats. All in one place" },
              { Screen: QRScreen, label: "QR Code Attendance", sub: "Print once, stick at entrance. Never changes" },
              { Screen: MenuScreen, label: "Daily Menu", sub: "Add menus in Hindi or Marathi" },
            ].map(({ Screen, label, sub }, i) => (
              <Reveal key={label} delay={i * 120}>
                <div style={{ textAlign: "center" }}>
                  <PhoneShell><Screen /></PhoneShell>
                  <div style={{ marginTop: 20, fontWeight: 700, fontSize: 15, color: C.dark }}>{label}</div>
                  <div style={{ fontSize: 14, color: C.muted, marginTop: 6, maxWidth: 200, margin: "6px auto 0" }}>{sub}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how" className="sec">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <div style={{ display: "inline-block", background: C.orangeLight, color: C.orange, borderRadius: 20, padding: "5px 16px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Simple Process</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,4vw,48px)", fontWeight: 700, marginBottom: 12 }}>Go Digital in 3 Simple Steps</h2>
            </div>
          </Reveal>
          <div className="steps-grid">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 130}>
                <div className="step-card">
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 52, fontWeight: 700, color: C.border, lineHeight: 1, marginBottom: 4 }}>{s.num}</div>
                  <div style={{ fontSize: 36, marginBottom: 14 }}>{s.icon}</div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, marginBottom: 10, color: C.dark }}>{s.title}</h3>
                  <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.7 }}>{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TIER 2 FEATURES ═══ */}
      <section style={{ background: C.charcoal }} className="sec">
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 52 }}>
              <div style={{ display: "inline-block", background: "rgba(232,124,12,0.2)", color: C.orange, borderRadius: 20, padding: "5px 16px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>More Features</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,4vw,48px)", fontWeight: 700, marginBottom: 12, color: "#fff" }}>And There's a Lot More</h2>
              <p style={{ color: "#c8b4a0", fontSize: 17, maxWidth: 480, margin: "0 auto" }}>All of these are included in your ₹2999/month plan. Nothing extra.</p>
            </div>
          </Reveal>
          <div className="feat3">
            {tier2.map((f, i) => (
              <Reveal key={f.title} delay={i * 70}>
                <div className="feat-card-dark">
                  <div style={{ fontSize: 28, marginBottom: 12 }}>{f.icon}</div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 700, marginBottom: 8, color: "#fff" }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: "#c8b4a0", lineHeight: 1.65 }}>{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="sec" style={{ background: C.orangeLight }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div style={{ display: "inline-block", background: "#fff", color: C.orange, borderRadius: 20, padding: "5px 16px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Transparent Pricing</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,4vw,48px)", fontWeight: 700, marginBottom: 12 }}>Simple, honest pricing</h2>
              <p style={{ color: C.muted, fontSize: 17 }}>No hidden charges. No surprise bills. Ever.</p>
            </div>
          </Reveal>
          <div className="pricing-grid">
            {/* Free Trial Card */}
            <Reveal>
              <div style={{ background: "#fff", borderRadius: 20, padding: "36px 32px", boxShadow: "0 8px 40px rgba(0,0,0,0.08)", border: `2px solid ${C.orange}`, position: "relative" }}>
                <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: C.orange, color: "#fff", borderRadius: 20, padding: "4px 18px", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>🎉 Start Here</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 42, fontWeight: 700, color: C.orange, lineHeight: 1, marginBottom: 4 }}>FREE</div>
                <div style={{ fontSize: 15, color: C.muted, marginBottom: 24 }}>90 days. No credit card</div>
                {["QR Code Attendance", "Live Dashboard", "Subscription Management", "Open/Close Toggle", "Special Menu Posting", "Student Discovery", "Hindi / Marathi Support"].map(f => (
                  <div key={f} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "center" }}>
                    <span style={{ color: C.green, fontSize: 16 }}>✓</span>
                    <span style={{ fontSize: 14, color: C.dark }}>{f}</span>
                  </div>
                ))}
                <button onClick={() => window.open("https://swaadindia.co.in/vendor-registration/", "_blank")} style={{
                  marginTop: 24, width: "100%", background: C.orange, color: "#fff", border: "none",
                  borderRadius: 12, padding: "14px", fontSize: 16, fontWeight: 700,
                  fontFamily: "'Jost',sans-serif", cursor: "pointer"
                }}>Start Free. Register Now →</button>
              </div>
            </Reveal>
            {/* Paid Card */}
            <Reveal delay={120}>
              <div style={{ background: "#fff", borderRadius: 20, padding: "36px 32px", boxShadow: "0 8px 40px rgba(0,0,0,0.08)" }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 42, fontWeight: 700, color: C.dark, lineHeight: 1 }}>₹2999</div>
                <div style={{ fontSize: 15, color: C.muted, marginBottom: 6 }}>per month. After 90 days</div>
                <div style={{ fontSize: 13, color: C.green, fontWeight: 600, marginBottom: 24 }}>All features included · Cancel anytime</div>
                {["Everything in free trial", "Order Management", "Expense Tracker", "Monthly Revenue Reports", "Billing Dashboard", "Vendor Attendance History", "Priority Support"].map(f => (
                  <div key={f} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "center" }}>
                    <span style={{ color: C.orange, fontSize: 16 }}>✓</span>
                    <span style={{ fontSize: 14, color: C.dark }}>{f}</span>
                  </div>
                ))}
                <div style={{ marginTop: 24, background: C.orangeLight, borderRadius: 10, padding: "12px 16px", fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                  You won't be charged automatically after 90 days. We'll reach out and renewal is entirely your choice.
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="sec">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div style={{ display: "inline-block", background: C.orangeLight, color: C.orange, borderRadius: 20, padding: "5px 16px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Vendor Reviews</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,4vw,48px)", fontWeight: 700, marginBottom: 12 }}>What Vendors Are Saying</h2>
              <p style={{ color: C.muted, fontSize: 17 }}>Pune ke mess owners ki real stories. Coming soon as we onboard our first vendors.</p>
            </div>
          </Reveal>
          <div className="testi-grid">
            {[
              { name: "Shree Bhojanalay", area: "Kothrud, Pune", quote: "Stopped using the attendance register within a week of installing the app. Very easy to use." },
              { name: "Maa Tiffin Centre", area: "Wakad, Pune", quote: "Students subscribe on their own now. No more chasing them on WhatsApp for renewals." },
              { name: "Ghar Ka Swaad", area: "Punawale, Pune", quote: "Printed the QR, stuck it up. Attendance is automatic. Life got simple." },
            ].map((t, i) => (
              <Reveal key={t.name} delay={i * 100}>
                <div style={{ background: "#fff", borderRadius: 16, padding: "28px 24px", boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: `1px solid ${C.border}`, position: "relative" }}>
                  <div style={{ position: "absolute", top: -1, left: -1, right: -1, height: 4, background: C.orange, borderRadius: "16px 16px 0 0" }} />
                  <div style={{ fontSize: 28, marginBottom: 14, color: C.orange }}>"</div>
                  <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.7, marginBottom: 20, fontStyle: "italic" }}>{t.quote}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.orangeLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>👨‍🍳</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.dark }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: C.muted }}>{t.area}</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, fontSize: 11, color: "#bbb", fontStyle: "italic" }}>* Placeholder. Real quote coming soon</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FOR STUDENTS ═══ */}
      <section id="students" className="sec" style={{ background: C.charcoal }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="student-grid">
            <Reveal>
              <div>
                <div style={{ display: "inline-block", background: "rgba(232,124,12,0.2)", color: C.orange, borderRadius: 20, padding: "5px 16px", fontSize: 13, fontWeight: 600, marginBottom: 20 }}>For Students</div>
                <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 700, color: "#fff", marginBottom: 16, lineHeight: 1.2 }}>
                  Find a Mess Near You. With Live Menu Updates
                </h2>
                <p style={{ color: "#c8b4a0", fontSize: 16, lineHeight: 1.7, marginBottom: 28 }}>
                  30-day subscriptions, QR attendance, and real-time menus. Sab ek app mein. Covering Kothrud, Wakad, Punawale, and every area across Pune.
                </p>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <DlBtn icon="🤖" store="Google Play" sub="Get it on" href="https://play.google.com/store/apps/details?id=com.swaad.india" />
                  <DlBtn icon="📦" store="Download APK" sub="Android direct" href="/swaad-india.apk" light />
                </div>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {[
                  { icon: "🗺️", t: "Find Nearby Mess", b: "Location-based search" },
                  { icon: "📋", t: "Live Daily Menu", b: "Before you step out" },
                  { icon: "📅", t: "30-Day Plans", b: "Flexible subscriptions" },
                  { icon: "📱", t: "QR Attendance", b: "Tap and mark. Done" },
                ].map(f => (
                  <div key={f.t} style={{ background: "rgba(255,255,255,0.07)", borderRadius: 12, padding: "16px 14px", border: "1px solid rgba(255,255,255,0.1)" }}>
                    <div style={{ fontSize: 22, marginBottom: 8 }}>{f.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{f.t}</div>
                    <div style={{ fontSize: 12, color: "#c8b4a0" }}>{f.b}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══ WHATSAPP CTA BAND ═══ */}
      <div style={{ background: C.wa, padding: "40px 24px", textAlign: "center" }}>
        <Reveal>
          <div style={{ fontSize: 28, marginBottom: 8 }}>💬</div>
          <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 32, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Have Questions? WhatsApp Us</h3>
          <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 16, marginBottom: 24 }}>We respond better on WhatsApp and faster too. Guaranteed reply within 30 minutes.</p>
          <a href="https://wa.me/917030078393?text=Hi%2C%20I%27m%20interested%20in%20SwaadIndia%20for%20my%20mess"
            target="_blank" rel="noopener noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#fff", color: C.wa, borderRadius: 14, padding: "14px 32px", fontSize: 16, fontWeight: 700, textDecoration: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.15)", fontFamily: "'Jost',sans-serif" }}>
            <span style={{ fontSize: 22 }}>💬</span> WhatsApp: 7030078393
          </a>
        </Reveal>
      </div>

      {/* ═══ ABOUT US ═══ */}
      <section id="about" className="sec" style={{ background: C.orangeLight }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <div style={{ display: "inline-block", background: "#fff", color: C.orange, borderRadius: 20, padding: "5px 16px", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>About Us</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,4vw,52px)", fontWeight: 700, marginBottom: 12, color: C.dark }}>Built in Pune, for Pune</h2>
              <p style={{ color: C.muted, fontSize: 17, maxWidth: 540, margin: "0 auto" }}>We're a small team that saw mess owners struggling with paperwork every single day and decided to fix it.</p>
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "start" }} className="about-grid">
            {/* Left: Story */}
            <Reveal>
              <div>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 700, color: C.dark, marginBottom: 16 }}>Our Story</h3>
                <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.8, marginBottom: 16 }}>
                  SwaadIndia was born out of a simple observation: mess owners in Pune were spending hours every week managing attendance registers, chasing students for renewals, and answering "aaj kya hai?" calls.
                </p>
                <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.8, marginBottom: 16 }}>
                  We're <strong style={{ color: C.dark }}>Premontix</strong>, a Pune-based tech startup. We built SwaadIndia to give mess owners a real digital tool. Not a generic SaaS, but something designed specifically for the Pune mess ecosystem.
                </p>
                <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.8 }}>
                  From QR attendance to subscription management, every feature exists because a real mess owner asked for it. We speak Marathi, we understand the local mess business, and we're here for the long run.
                </p>
              </div>
            </Reveal>

            {/* Right: Mission cards + founder */}
            <Reveal delay={150}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  { icon: "🎯", title: "Our Mission", body: "Make every mess in Pune paperless. One QR code at a time. We want owners to spend time cooking great food, not managing registers." },
                  { icon: "💡", title: "Our Vision", body: "A Pune where every student can find a great home-style mess in two taps, and every mess owner runs a fully-digital business from their phone." },
                  { icon: "🤝", title: "Our Promise", body: "90 days free, no credit card, no pressure. We earn your trust before we ask for anything. And we'll always answer a WhatsApp message within 30 minutes." },
                ].map(c => (
                  <div key={c.title} style={{ background: "#fff", borderRadius: 16, padding: "22px 24px", boxShadow: "0 4px 16px rgba(0,0,0,0.05)", border: `1px solid ${C.border}`, display: "flex", gap: 16, alignItems: "flex-start" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: C.orangeLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{c.icon}</div>
                    <div>
                      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 700, color: C.dark, marginBottom: 6 }}>{c.title}</div>
                      <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, margin: 0 }}>{c.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Bottom: Values row */}
          <Reveal delay={200}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginTop: 48 }} className="values-grid">
              {[
                { icon: "🏙️", label: "Pune-first", sub: "Built for local mess culture" },
                { icon: "🔓", label: "No Lock-in", sub: "Cancel any time, no hassle" },
                { icon: "🗣️", label: "Local Language", sub: "Marathi & Hindi supported" },
                { icon: "⚡", label: "Always Improving", sub: "New features every month" },
              ].map(v => (
                <div key={v.label} style={{ background: "#fff", borderRadius: 16, padding: "24px 20px", textAlign: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.05)", border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>{v.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: C.dark, marginBottom: 6 }}>{v.label}</div>
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{v.sub}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
      <style>{`.about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start; } .values-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 20px; } @media(max-width:900px){ .about-grid { grid-template-columns: 1fr !important; gap: 32px; } .values-grid { grid-template-columns: repeat(2,1fr) !important; } } @media(max-width:600px){ .values-grid { grid-template-columns: repeat(2,1fr) !important; } }`}</style>

      {/* ═══ CONTACT ═══ */}
      <section id="contact" className="sec">
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div style={{ display: "inline-block", background: "transparent", color: C.orange, border: `1px solid ${C.orange}`, borderRadius: 20, padding: "5px 16px", fontSize: 13, fontWeight: 600, marginBottom: 20 }}>Get in Touch</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(28px,4vw,52px)", fontWeight: 700, marginBottom: 12, color: C.dark }}>We'd love to hear from you</h2>
              <p style={{ color: C.orange, fontSize: 16, fontWeight: 500 }}>Student query, mess registration, or partnership. Reach out any time.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div style={{ background: "#fff", borderRadius: 24, padding: "48px 40px", boxShadow: "0 8px 40px rgba(0,0,0,0.07)", border: `1px solid ${C.border}` }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 48 }} className="contact-form-grid">
                {/* Left: Contact Info */}
                <div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, fontWeight: 700, color: C.dark, marginBottom: 28 }}>Contact Information</h3>
                  {[
                    { icon: "✉️", label: "Email", val: "swaadindia.app@gmail.com" },
                    { icon: "📍", label: "Location", val: "Pune, Maharashtra" },
                    { icon: "⏰", label: "Response time", val: "Within 24 hours" },
                  ].map(c => (
                    <div key={c.label} style={{ display: "flex", gap: 14, marginBottom: 24, alignItems: "flex-start" }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: C.bg, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{c.icon}</div>
                      <div>
                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>{c.label}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.dark }}>{c.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Right: Form */}
                <form onSubmit={handleContactSubmit}>
                  <div className="contact-grid" style={{ marginBottom: 14 }}>
                    <input
                      type="text" placeholder="Your Name" required
                      value={contactForm.name}
                      onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))}
                      style={{ padding: "13px 16px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: "'Jost',sans-serif", outline: "none", color: C.dark, background: "#fafafa" }}
                    />
                    <input
                      type="email" placeholder="Email Address" required
                      value={contactForm.email}
                      onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                      style={{ padding: "13px 16px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: "'Jost',sans-serif", outline: "none", color: C.dark, background: "#fafafa" }}
                    />
                  </div>
                  <input
                    type="tel" placeholder="Phone Number (optional)"
                    value={contactForm.phone}
                    onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))}
                    style={{ width: "100%", padding: "13px 16px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: "'Jost',sans-serif", outline: "none", color: C.dark, background: "#fafafa", marginBottom: 14, boxSizing: "border-box" }}
                  />
                  <textarea
                    placeholder="Tell us about your mess / query..." required rows={5}
                    value={contactForm.message}
                    onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))}
                    style={{ width: "100%", padding: "13px 16px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, fontFamily: "'Jost',sans-serif", outline: "none", color: C.dark, background: "#fafafa", resize: "vertical", marginBottom: 20, boxSizing: "border-box" }}
                  />
                  {contactSent && (
                    <div style={{ background: C.greenLight, color: C.green, borderRadius: 10, padding: "12px 16px", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
                      ✅ Message sent! We'll get back to you within 24 hours.
                    </div>
                  )}
                  {contactError && (
                    <div style={{ background: "#FEE2E2", color: "#991B1B", borderRadius: 10, padding: "12px 16px", fontSize: 14, fontWeight: 500, marginBottom: 16 }}>
                      ⚠️ {contactError}
                    </div>
                  )}
                  <button type="submit" disabled={contactLoading} style={{
                    background: contactLoading ? C.muted : C.orange, color: "#fff", border: "none", borderRadius: 12,
                    padding: "14px 32px", fontSize: 15, fontWeight: 700, fontFamily: "'Jost',sans-serif",
                    cursor: contactLoading ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 8,
                    opacity: contactLoading ? 0.7 : 1
                  }}>{contactLoading ? "Sending..." : "Send Message →"}</button>
                </form>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <style>{`.contact-form-grid { display: grid; grid-template-columns: 1fr 1.5fr; gap: 48px; } @media(max-width:768px){ .contact-form-grid { grid-template-columns: 1fr !important; gap: 32px; } }`}</style>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ background: C.charcoal, padding: "64px 24px 0", color: "#fff" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="footer-grid" style={{ paddingBottom: 48, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
            <div>
              <div style={{ marginBottom: 16 }}>
                <img src="/swaadIndia_logo.png" alt="SwaadIndia" style={{ height: 58, width: "auto", display: "block" }} />
              </div>
              <p style={{ color: "#c8b4a0", fontSize: 14, lineHeight: 1.7, marginBottom: 8, maxWidth: 260 }}>
                Pune's digital partner for mess owners. From QR attendance to subscription management. All in one place.
              </p>
              <p style={{ color: "#9a8070", fontSize: 12, marginBottom: 20 }}>Udyam Registered · Premontix</p>
              <a href="https://instagram.com/swaaadindia" target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.1)", borderRadius: 8, padding: "8px 14px", textDecoration: "none", color: "#c8b4a0", fontSize: 13 }}>
                📸 @swaaadindia
              </a>
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 16, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.08em", color: C.orange }}>Navigate</div>
              {[...navLinks, { label: "Register Mess", id: "pricing" }].map(n => (
                <div key={n.id} style={{ marginBottom: 10 }}>
                  <a href={`#${n.id}`} onClick={e => { e.preventDefault(); scrollTo(n.id); }}
                    style={{ color: "#c8b4a0", textDecoration: "none", fontSize: 14 }}>{n.label}</a>
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 16, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.08em", color: C.orange }}>Legal</div>
              {["Privacy Policy", "Terms of Service", "Refund Policy", "Cookie Policy"].map(l => (
                <div key={l} style={{ marginBottom: 10 }}>
                  {l === "Privacy Policy"
                    ? <button onClick={() => { window.scrollTo(0,0); setPage("privacy"); }} style={{ background: "none", border: "none", padding: 0, color: "#c8b4a0", fontSize: 14, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>{l}</button>
                    : <span style={{ color: "#c8b4a0", fontSize: 14, cursor: "default" }}>{l}</span>
                  }
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 16, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.08em", color: C.orange }}>Contact</div>
              {[
                { icon: "📧", val: "swaadindia.app@gmail.com" },
                { icon: "📞", val: "7030078393" },
                { icon: "📞", val: "7276778546" },
                { icon: "📍", val: "Punawale, Pune 411033" },
              ].map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 13 }}>{c.icon}</span>
                  <span style={{ color: "#c8b4a0", fontSize: 13, lineHeight: 1.5 }}>{c.val}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: "20px 0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <p style={{ color: "#9a8070", fontSize: 13 }}>© {new Date().getFullYear()} SwaadIndia · Premontix. All rights reserved. Made with ❤️ in Pune.</p>
            <p style={{ color: "#9a8070", fontSize: 13 }}>Helping mess owners grow, one subscription at a time.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
